import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Sparkles, FileUp, MessageCircle, ShieldCheck, Bell, CalendarDays,
  Rocket, GraduationCap, Bot, TrendingUp, Building2, Landmark,
  CreditCard, Briefcase, Wallet, Target, LineChart,
  Lock, Server, KeyRound, FileCheck2, EyeOff, ShieldAlert,
  Check, X, Star, Quote, ChevronRight,
  UserPlus, FolderInput, FileSearch, Gavel, Send, MailCheck,
  RefreshCw, CircleDollarSign, Trophy, Percent, Clock, BadgeCheck,
} from "lucide-react";

/* ==============================================================
   Uploaded brand assets — used for educational/informational reference only.
   Credit Quack does not claim endorsement, sponsorship, or partnership.
   ============================================================== */
export const BRAND_ASSETS = {
  experian:      "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/zkuz0cnr_Experian_logo.svg.webp",
  equifax:       "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/yj73dt88_Equifax_Logo.svg.webp",
  transunion:    "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/h98v33kq_TransUnion_logo.svg.webp",
  amexWordmark:  "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/nnj9cj6j_American-Express-Color.png",
  amexCards:     "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/zzrpf9gi_American-Express-Card-PNG-Image.png",
  navyFederalCards: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/8xrvy9sy_credit-card-fan-billboard-v3.webp",
  cardFan:       "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/8xrvy9sy_credit-card-fan-billboard-v3.webp",
  cardsMixed:    "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/bcnh7tlf_cancel-business-cards.jpg",
  pnc:           "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/c66cj11c_pnc-bank-logo-0.webp",
  tdBank:        "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/8jqdetgo_TD_Bank%2C_N.A.-Logo.wine.webp",
  sunTrust:      "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/egcmlq92_SunTrust_Banks-Logo.wine.webp",
  discoverPersonalLoans: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/snomr99h_Discover_Personal_Loans.png",
  sbaLogo:       "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/zpnkwfcx_SBA-Logo-Stacked.png",
  trustBadgesStrip: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/uji2x8sv_Credit-Card-Trust-Badges-Download-Transparent-PNG-Image.png",
  bankOfAmerica: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/gsg8rkv2_BankOfAmerica.Logo_.webp",
  bbb:           "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/kzax21s5_8e6afa40ca54f627df7698cf9a122ef6eeebd18c-602e3757d9ced200045a579b.png",
  mcafeeSecure:  "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/a8l9ki2m_McAfee%C2%AESecure-Logo-Vector.svg-.png",
  headshotPriya: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/62fbbjls_image-asset.webp",
  headshotAisha: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/uqlp8iv5_Headshots%2BOKC3.webp",
  headshotMarcus:"https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/ie19j47j_images%20%283%29.jpeg",
  trustpilot:    "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/7zwzp903_SAuVO.png",
  dunBradstreet: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/96t0teua_DB_WORDMARK_Pantone.png",
  identityIQ:    "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/s3rs4grm_unnamed.png",
  navyFederal:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/zmqxci7z_images%20%281%29.png",
  photoXzavier:  "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/ol51wuka_Screenshot%202026-07-08%20at%201.59.36%E2%80%AFAM.png",
  photoLorenzo:  "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/yt5z770j_Screenshot%202026-07-08%20at%202.00.34%E2%80%AFAM.png",
  photoMone:     "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/hvmrekyn_PHOTO-2026-09-24-00-55-25.jpg",
};

const REFERENCE_DISCLAIMER = "Logos shown for educational and informational reference only. Credit Quack is not claiming official endorsement, sponsorship, or partnership unless specifically stated.";

/* ==============================================================
   Trust bar — bureaus + tech stack
   Uses uploaded Experian + Equifax logos + a matching TransUnion tile for the 3rd bureau.
   Reference / educational only — no claim of partnership.
   ============================================================== */
export function TrustBar() {
  const bureaus = [
    { name: "Experian",   logo: BRAND_ASSETS.experian,   tag: "Credit Bureau" },
    { name: "Equifax",    logo: BRAND_ASSETS.equifax,    tag: "Credit Bureau" },
    { name: "TransUnion", logo: BRAND_ASSETS.transunion, tag: "Credit Bureau" },
  ];
  const partners = [
    { name: "IdentityIQ",     tag: "Monitoring"     },
    { name: "IdentityWorks",  tag: "Monitoring"     },
    { name: "Stripe",         tag: "Payments"       },
    { name: "Plaid",          tag: "Bank Data"      },
    { name: "Twilio",         tag: "Messaging"      },
    { name: "SendGrid",       tag: "Email"          },
    { name: "AWS",            tag: "Cloud"          },
    { name: "Google Cloud",   tag: "Cloud"          },
    { name: "Zapier",         tag: "Automation"     },
  ];
  const doubled = [...partners, ...partners];
  return (
    <section className="py-14 lg:py-16 bg-white border-y border-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs uppercase tracking-[0.28em] font-bold text-slate-400">Credit ecosystem &amp; trusted technologies</p>
        <p className="text-center text-slate-600 mt-2 max-w-2xl mx-auto">Credit Quack helps clients understand and organize information commonly reported by major credit bureaus.</p>

        {/* Bureau tiles — full-size branded */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mt-8 max-w-3xl mx-auto">
          {bureaus.map((b) => (
            <div key={b.name} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-center h-24 sm:h-28 shadow-sm hover:shadow-md hover:border-slate-300 transition" data-testid={`bureau-tile-${b.name.toLowerCase()}`}>
              <img src={b.logo} alt={`${b.name} logo — reference only`} className="max-h-10 sm:max-h-12 w-auto object-contain" loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {/* Partner tech marquee */}
      <div className="relative mt-10 overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)" }}>
        <div className="cq-marquee-track w-max py-2">
          {doubled.map((p, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white grid place-items-center font-black text-sm">
                {p.name.slice(0,1)}
              </div>
              <div className="whitespace-nowrap">
                <p className="font-display font-black text-slate-900 leading-none">{p.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">{p.tag}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-center text-[11px] text-slate-400 mt-4 px-4 max-w-3xl mx-auto">{REFERENCE_DISCLAIMER}</p>
    </section>
  );
}

/* ==============================================================
   Dashboard mockup — pure CSS/SVG illustration (no external images)
   ============================================================== */
export function DashboardShowcase({ scrollToSignup }) {
  return (
    <section id="dashboard-preview" className="relative py-20 lg:py-28 bg-slate-950 text-white overflow-hidden">
      {/* backdrop */}
      <div className="cq-aurora absolute inset-0 opacity-70" />
      <div className="cq-dotgrid absolute inset-0 opacity-[0.08]" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-amber-500/20 to-transparent blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-300">Inside the platform</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05]">
            One dashboard. <span className="cq-text-gradient-gold">Every step in real time.</span>
          </h2>
          <p className="text-slate-300 text-lg mt-4 max-w-2xl mx-auto">Progress, documents, messaging, credit analysis, funding readiness, appointments, and AI recommendations — unified.</p>
        </div>

        {/* Device stack */}
        <div className="relative mt-16 grid lg:grid-cols-12 gap-8 items-start">
          {/* Left cards column */}
          <div className="lg:col-span-3 space-y-4">
            <MockCard title="Funding Readiness" subtitle="Live score">
              <div className="flex items-end justify-between">
                <p className="font-display text-5xl font-black text-white">742</p>
                <span className="text-xs font-bold text-emerald-400">+38 · 30d</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 mt-3 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600 cq-progress-fill" style={{ width: "74%" }} />
              </div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 mt-2">Strong</p>
            </MockCard>

            <MockCard title="Progress Tracker" subtitle="Step 6 of 10">
              <ul className="space-y-1.5">
                {["Documents", "Analysis", "Disputes Sent", "Responses"].map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-4 h-4 rounded-full ${i < 3 ? "bg-emerald-500" : "bg-amber-400 cq-pulse"} grid place-items-center`}>
                      {i < 3 && <Check size={9} className="text-white" />}
                    </span>
                    <span className={i < 3 ? "text-slate-300" : "text-white font-semibold"}>{s}</span>
                  </li>
                ))}
              </ul>
            </MockCard>
          </div>

          {/* Center — main dashboard */}
          <div className="lg:col-span-6">
            <div className="relative cq-scale-in">
              <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-white/10 shadow-2xl overflow-hidden">
                {/* Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900/80 border-b border-white/5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                  <div className="ml-3 rounded-md bg-white/5 px-3 py-1 text-[10px] text-slate-300 font-mono">clientquack.com/portal</div>
                </div>
                <div className="p-6">
                  {/* Top hero */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Welcome back</p>
                      <p className="font-display text-2xl font-black text-white leading-tight">Jamie's Credit Journey</p>
                    </div>
                    <div className="rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold px-3 py-1">All systems on track</div>
                  </div>
                  {/* 10-step tracker */}
                  <div className="mt-5 flex items-center gap-1">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full ${i < 6 ? "bg-gradient-to-r from-amber-400 to-amber-500" : "bg-white/10"}`} />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 text-[9px] uppercase tracking-widest text-slate-400 font-bold">
                    <span>Start</span><span>Analysis</span><span>Disputes</span><span>Funding</span>
                  </div>

                  {/* Two-column stats */}
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    <MiniStat label="Docs approved" value="14 / 16" trend="up" />
                    <MiniStat label="Pending disputes" value="3" trend="flat" />
                    <MiniStat label="Est. funding" value="$85k" trend="up" />
                    <MiniStat label="Next update" value="Tomorrow" trend="flat" />
                  </div>

                  {/* Message + AI */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300">Specialist</p>
                      <p className="text-xs text-white mt-1">"New round of disputes filed today — expecting responses in 14 days."</p>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">AI Assistant</p>
                      <p className="text-xs text-white mt-1">Pay down $420 on card #2 this month to boost your score ~18 pts.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating phone */}
              <div className="hidden lg:block absolute -bottom-10 -right-14 w-56 rounded-[36px] bg-slate-900 border-[3px] border-white/10 shadow-2xl cq-float p-2">
                <div className="rounded-[28px] overflow-hidden bg-slate-950 h-[440px] p-4 flex flex-col">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">On the go</p>
                  <p className="font-display text-lg font-black text-white leading-tight mt-1">Score: 742</p>
                  <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-amber-400 to-amber-600" />
                  </div>
                  <div className="mt-4 space-y-2 flex-1">
                    {["Uploaded ID.pdf", "Dispute #4 responded", "Booked call with Aria"].map((t, i) => (
                      <div key={i} className="rounded-lg bg-white/5 p-2 text-[11px] text-slate-200">{t}</div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-amber-500 text-white text-xs font-black py-2 text-center mt-3">
                    Book meeting
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right cards column */}
          <div className="lg:col-span-3 space-y-4">
            <MockCard title="Document Vault" subtitle="Encrypted">
              <ul className="space-y-2 text-xs">
                {[
                  { n: "Photo ID.pdf",   c: "text-emerald-400", s: "Verified" },
                  { n: "SSN Card.pdf",   c: "text-emerald-400", s: "Verified" },
                  { n: "Pay Stub.pdf",   c: "text-amber-300",   s: "Reviewing" },
                  { n: "Utility Bill",   c: "text-slate-400",   s: "Pending" },
                ].map((d, i) => (
                  <li key={i} className="flex justify-between items-center">
                    <span className="text-slate-200 truncate">{d.n}</span>
                    <span className={`text-[10px] font-bold ${d.c}`}>{d.s}</span>
                  </li>
                ))}
              </ul>
            </MockCard>

            <MockCard title="Upcoming Meeting" subtitle="Confirmed">
              <p className="font-display text-lg font-black text-white leading-tight">Funding Review</p>
              <p className="text-xs text-slate-300 mt-1">Tue, 2:00 PM · Aria Chen</p>
              <div className="rounded-lg bg-amber-500/15 border border-amber-400/30 text-amber-200 text-[10px] font-bold px-2 py-1 mt-2 inline-block">30 min</div>
            </MockCard>
          </div>
        </div>

        {/* Feature ribbon */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: TrendingUp, label: "Live tracker" },
            { icon: FileUp, label: "Document vault" },
            { icon: MessageCircle, label: "Secure chat" },
            { icon: Bot, label: "AI assistant" },
          ].map((f, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-300 grid place-items-center">
                <f.icon size={18} />
              </div>
              <p className="text-sm font-bold text-white">{f.label}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-14">
          <button onClick={scrollToSignup} className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black text-base h-14 px-8 shadow-lg hover:shadow-amber-500/30 transition" data-testid="dashboard-cta">
            Get my client dashboard <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function MockCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 backdrop-blur cq-float-slow">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{title}</p>
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">{subtitle}</p>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function MiniStat({ label, value, trend }) {
  const color = trend === "up" ? "text-emerald-400" : "text-slate-400";
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
      <p className="text-[9px] uppercase tracking-widest font-bold text-slate-400">{label}</p>
      <p className="font-display text-lg font-black text-white mt-0.5">{value}</p>
      <p className={`text-[10px] font-bold ${color}`}>{trend === "up" ? "▲ growing" : "— steady"}</p>
    </div>
  );
}

/* ==============================================================
   Feature grid — 8 premium features
   ============================================================== */
export function FeaturesGrid() {
  const features = [
    { icon: TrendingUp,   title: "Live progress tracking",       desc: "Know exactly where your file stands 24/7. Every status change is timestamped and pushed to your dashboard the instant your specialist logs it.", accent: "amber",   testid: "feat-tracking"       },
    { icon: FileUp,       title: "Secure document vault",         desc: "Upload IDs, statements, tax documents, and supporting files with a single click. Encrypted at rest and in transit.",                        accent: "sky",     testid: "feat-vault"           },
    { icon: MessageCircle,title: "Direct specialist messaging",   desc: "Skip the phone tag. Message your assigned specialist directly and see every reply in one thread — no lost emails.",                        accent: "emerald", testid: "feat-messaging"       },
    { icon: Wallet,       title: "Funding readiness dashboard",   desc: "Track your overall funding profile and get personalized recommendations for cards, SBA loans, and lines of credit — before you apply.",     accent: "amber",   testid: "feat-funding"         },
    { icon: GraduationCap,title: "Credit education center",       desc: "Built-in learning modules on utilization, tradelines, and disputes so you understand every move the team makes on your behalf.",           accent: "sky",     testid: "feat-education"       },
    { icon: Bell,         title: "Real-time notifications",       desc: "Instant push, email, and in-app alerts whenever a document is reviewed, a dispute is filed, or a bureau responds. No refreshes needed.",     accent: "rose",    testid: "feat-notifications"   },
    { icon: CalendarDays, title: "Appointment scheduling",        desc: "Book consultations directly inside your dashboard from 30-min slots. Reason-driven meetings with automatic reminders.",                    accent: "indigo",  testid: "feat-scheduling"      },
    { icon: Bot,          title: "AI credit assistant",           desc: "AI-powered recommendations tuned to your credit profile, utilization, and cash flow. Ask questions in plain English, get concrete steps.",   accent: "emerald", testid: "feat-ai"              },
  ];
  const accentMap = {
    amber:   "bg-amber-50   text-amber-700   ring-amber-100",
    sky:     "bg-sky-50     text-sky-700     ring-sky-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    rose:    "bg-rose-50    text-rose-700    ring-rose-100",
    indigo:  "bg-indigo-50  text-indigo-700  ring-indigo-100",
  };
  return (
    <section id="features" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Complete toolkit</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Everything a modern credit journey should include.
          </h2>
          <p className="text-slate-600 text-lg mt-4">Eight capabilities built to eliminate friction — from the first document you upload to the moment you hit your funding goal.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {features.map((f, i) => (
            <Card key={i} className="p-6 rounded-2xl border-slate-100 cq-lift bg-white h-full" data-testid={f.testid}>
              <div className={`w-12 h-12 rounded-2xl ring-1 grid place-items-center ${accentMap[f.accent]}`}>
                <f.icon size={22} />
              </div>
              <h3 className="font-display text-xl font-black text-slate-900 mt-5 leading-tight">{f.title}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Funding Center — flagship feature block with Amex + Navy Federal card imagery
   ============================================================== */
export function FundingCenterBlock() {
  const items = [
    { icon: CircleDollarSign, k: "Estimated Funding Amount",        v: "AI-scored across 21 products"           },
    { icon: CreditCard,       k: "Personal Credit Cards",            v: "Cards matched to your profile"          },
    { icon: Briefcase,        k: "Business Credit Cards",            v: "Ink, Amex Biz, Spark, more"             },
    { icon: Landmark,         k: "SBA Loan Information",             v: "Micro, 7(a), 504 side-by-side"          },
    { icon: Building2,        k: "Business Loan Marketplace",        v: "Fintech + bank lenders compared"        },
    { icon: Wallet,           k: "Personal Loan Marketplace",        v: "APR + probability transparency"         },
    { icon: Target,           k: "Approval Probability",             v: "Live model, not guesswork"              },
    { icon: LineChart,        k: "Estimated Interest Rates",         v: "Range per lender + your profile"        },
    { icon: CalendarDays,     k: "Funding Timeline",                 v: "Days-to-fund per opportunity"           },
    { icon: TrendingUp,       k: "Utilization Analysis",             v: "Optimal payment sequencing"             },
    { icon: Sparkles,         k: "Funding Readiness Score",          v: "0–100 tracked over time"                },
    { icon: Trophy,           k: "Personalized Improvement Plan",    v: "3–5 weekly actions ranked by ROI"       },
  ];
  const strategies = [
    { name: "Business Credit Cards",   sub: "Higher limits, separate from personal",       img: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/sr2s14wd_images.png",                            gradient: "from-slate-500/20 to-slate-800/5",     dark: false },
    { name: "Personal Credit Cards",   sub: "Rebuild + rewards paths",                      img: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/w68udq6u_Screen%20Shot%202016-12-20%20at%2010.43.47%20PM.png", gradient: "from-orange-500/20 to-orange-700/5", dark: false },
    { name: "Navy Federal Strategy",   sub: "Common credit union path clients ask about",  img: BRAND_ASSETS.navyFederalCards,                                                                                                                gradient: "from-sky-500/20 to-indigo-700/5",   dark: true  },
    { name: "American Express Strategy", sub: "Business + charge card structuring",         img: BRAND_ASSETS.amexCards,                                                                                                                       gradient: "from-yellow-500/20 to-orange-700/5", dark: true  },
    { name: "0% APR Opportunities",    sub: "Introductory-rate windows to leverage",        img: BRAND_ASSETS.cardsMixed,                                                                                                                      gradient: "from-fuchsia-500/20 to-purple-700/5", dark: true  },
    { name: "High-Limit Funding Paths", sub: "Limit-optimization approaches",               img: "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/7098cvn2_Venture%20this%20new.jpg",                                gradient: "from-rose-500/20 to-red-700/5",     dark: false },
  ];
  return (
    <section id="funding-center" className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header + hero cards */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 border border-amber-200 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-amber-700">
              <Rocket size={12} /> Flagship — Funding Center
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-4 leading-[1.05]">
              See the Funding Opportunities <br />
              <span className="cq-text-gradient-gold">Your Profile May Be Ready For</span>
            </h2>
            <p className="text-slate-600 text-lg mt-4">From personal credit cards to business cards, auto financing, lines of credit, and working capital — Credit Quack helps clients understand what funding paths may align with their credit profile.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {[
                "21 lenders + card products modeled with real approval math",
                "Readiness score updates the second your credit profile changes",
                "AI concierge suggests the top 3 next-best actions weekly",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-[32px] bg-slate-950 p-8 lg:p-10 overflow-hidden border border-slate-800 shadow-2xl">
              <div className="cq-aurora absolute inset-0 opacity-70" />
              <div className="relative flex items-center justify-center gap-4">
                <img src={BRAND_ASSETS.amexCards} alt="American Express business cards — reference only" className="w-56 lg:w-64 drop-shadow-[0_25px_45px_rgba(0,0,0,0.4)] cq-float" loading="lazy" />
                <img src={BRAND_ASSETS.navyFederalCards} alt="Navy Federal card family — reference only" className="w-56 lg:w-72 drop-shadow-[0_25px_45px_rgba(0,0,0,0.4)] cq-float-delay -ml-4" loading="lazy" />
              </div>
              <p className="relative text-[10px] text-slate-400 text-center mt-6 max-w-md mx-auto">{REFERENCE_DISCLAIMER}</p>
            </div>
          </div>
        </div>

        {/* Strategy cards using uploaded card imagery */}
        <div className="mt-16">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Strategy playbooks</p>
          <h3 className="font-display text-3xl sm:text-4xl font-black text-slate-900 mt-2">Funding brands clients often ask about.</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {strategies.map((s, i) => (
              <div key={i} className="rounded-3xl overflow-hidden border border-slate-200 bg-slate-950 cq-lift group" data-testid={`strategy-card-${i}`}>
                <div className={`relative h-40 bg-gradient-to-br ${s.gradient} overflow-hidden`}>
                  <div className="absolute inset-0 bg-slate-950/40" />
                  <img src={s.img} alt={`${s.name} — reference only`} className="absolute -bottom-4 -right-2 w-56 group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_15px_30px_rgba(0,0,0,0.5)]" loading="lazy" />
                </div>
                <div className="p-5">
                  <p className="font-display font-black text-white text-lg leading-tight">{s.name}</p>
                  <p className="text-xs text-slate-400 mt-1">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-4">Education around major lenders. Credit Quack is not officially partnered with American Express, Navy Federal Credit Union, or any bank shown; card imagery is for informational reference.</p>
        </div>

        {/* Feature tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-14">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 bg-white p-4 cq-lift">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 grid place-items-center shrink-0"><it.icon size={18} /></div>
                <div>
                  <p className="font-display font-black text-slate-900 leading-tight">{it.k}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{it.v}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Funding intelligence sub-cards — approval odds, limit, timing, bureau impact, etc. */}
        <div className="mt-14">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Inside every opportunity</p>
          <h3 className="font-display text-3xl sm:text-4xl font-black text-slate-900 mt-2">Every offer decoded, before you apply.</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: Target,       label: "Estimated Approval Odds",  v: "Live probability model" },
              { icon: CreditCard,   label: "Estimated Credit Limit",   v: "Range per issuer"       },
              { icon: Clock,        label: "Best Time to Apply",       v: "Score & TIB windows"    },
              { icon: LineChart,    label: "Bureau Impact",            v: "Which bureau pulls"     },
              { icon: BadgeCheck,   label: "Inquiry Risk",             v: "Soft vs hard scoring"   },
              { icon: Percent,      label: "Utilization Impact",       v: "New-limit effect"       },
              { icon: Briefcase,    label: "Business Readiness",       v: "PG risk analysis"       },
              { icon: FileCheck2,   label: "Required Documents",       v: "Checklist per lender"   },
            ].map((c, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-100 p-4 cq-lift" data-testid={`funding-insight-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-amber-300 grid place-items-center"><c.icon size={18} /></div>
                <p className="font-display font-black text-slate-900 mt-3 text-sm leading-tight">{c.label}</p>
                <p className="text-[11px] text-slate-500 mt-1">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Credit Monitoring section — 3 bureaus
   ============================================================== */
export function CreditMonitoringBlock() {
  const bureaus = [
    { name: "Experian",   logo: BRAND_ASSETS.experian,   score: 748, color: "from-sky-500 to-indigo-500",       change: "+18" },
    { name: "Equifax",    logo: BRAND_ASSETS.equifax,    score: 732, color: "from-emerald-500 to-emerald-600",  change: "+9"  },
    { name: "TransUnion", logo: BRAND_ASSETS.transunion, score: 754, color: "from-amber-500 to-orange-500",     change: "+22" },
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-sky-600">Credit Monitoring</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Three bureaus. <span className="text-sky-600">One screen.</span>
          </h2>
          <p className="text-slate-600 text-lg mt-4">Sample view of the 3-bureau monitoring surface. Credit Quack integrates with the platforms you already trust — no more logging into three different portals.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {bureaus.map((b) => (
            <Card key={b.name} className="rounded-3xl overflow-hidden border-slate-100 cq-lift" data-testid={`monitoring-card-${b.name.toLowerCase()}`}>
              <div className={`h-24 bg-gradient-to-br ${b.color}`} />
              <div className="p-6 -mt-14">
                <div className="w-24 h-24 rounded-2xl bg-white shadow-lg border border-slate-100 grid place-items-center p-3">
                  <img src={b.logo} alt={`${b.name} logo — reference only`} className="max-h-12 w-auto object-contain" loading="lazy" />
                </div>
                <div className="flex items-end justify-between mt-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{b.name}</p>
                    <p className="font-display text-5xl font-black text-slate-900 leading-none mt-1">{b.score}</p>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-xs font-black px-2 py-1 border border-emerald-100">
                    <TrendingUp size={11} className="mr-1" /> {b.change}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-3">Last pulled 2 hours ago · Sample data for illustration.</p>
              </div>
            </Card>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-6 max-w-3xl">
          Logos shown for educational and informational reference only. Credit Quack is not claiming official endorsement, sponsorship, accreditation, or partnership unless specifically stated in writing. Scores displayed are illustrative sample data.
        </p>
      </div>
    </section>
  );
}

/* ==============================================================
   Progress Timeline — 10 steps
   ============================================================== */
export function ProgressTimelineBlock() {
  const steps = [
    { icon: UserPlus,    t: "Account Created",         d: "Client signs up and completes onboarding." },
    { icon: FolderInput, t: "Documents Requested",     d: "Your specialist requests exactly what's needed." },
    { icon: FileCheck2,  t: "Documents Received",      d: "Uploads verified and filed in the vault." },
    { icon: FileSearch,  t: "Credit Analysis",         d: "Every tradeline, inquiry, and collection scored." },
    { icon: Gavel,       t: "Disputes Prepared",       d: "Custom dispute letters drafted per bureau." },
    { icon: Send,        t: "Disputes Sent",           d: "Certified mail + electronic delivery." },
    { icon: MailCheck,   t: "Creditor Responses",      d: "Every bureau reply logged in real time." },
    { icon: RefreshCw,   t: "Updates Posted",          d: "Your dashboard reflects each bureau change." },
    { icon: TrendingUp,  t: "Funding Review",          d: "You're prepped for the strongest available offers." },
    { icon: Trophy,      t: "Completed",               d: "Goals hit — you've got the tools to keep growing." },
  ];
  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Your journey</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Ten steps, zero mystery.
          </h2>
          <p className="text-slate-600 text-lg mt-4">Every credit journey follows the same rigorous path — you just get to watch the whole thing unfold.</p>
        </div>
        <div className="relative mt-14 grid md:grid-cols-2 gap-y-4 gap-x-16">
          {/* center line for large screens */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[3px] cq-timeline-line rounded-full -translate-x-1/2" />
          {steps.map((s, i) => (
            <div key={i} className={`relative flex md:${i % 2 === 0 ? "justify-end" : "justify-start"}`}>
              <div className="w-full md:w-[92%] rounded-2xl bg-white border border-slate-100 p-5 cq-lift shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 text-amber-700 grid place-items-center shrink-0">
                    <s.icon size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Step {i + 1}</p>
                    <p className="font-display text-lg font-black text-slate-900 leading-tight mt-0.5">{s.t}</p>
                    <p className="text-sm text-slate-600 mt-1">{s.d}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Why Credit Quack — 3 cards
   ============================================================== */
export function WhyChooseBlock() {
  const cards = [
    { icon: EyeOff,       t: "Transparency",     d: "Know exactly what's happening with your file. Every status change is timestamped, logged, and visible on your dashboard." },
    { icon: Sparkles,     t: "Technology",       d: "Modern tools built to simplify the credit repair process — encrypted vaults, real-time updates, AI-driven recommendations." },
    { icon: Rocket,       t: "Funding Focus",    d: "We don't stop at 'score fixed.' We prepare you for the exact loans, cards, and credit lines you'll qualify for next." },
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-emerald-600">Why Credit Quack</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Built differently — on purpose.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {cards.map((c, i) => (
            <Card key={i} className="p-8 rounded-3xl border-slate-100 cq-lift bg-gradient-to-br from-white to-slate-50">
              <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white grid place-items-center">
                <c.icon size={22} />
              </div>
              <h3 className="font-display text-2xl font-black text-slate-900 mt-5">{c.t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{c.d}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Comparison table — traditional vs Credit Quack
   ============================================================== */
export function ComparisonBlock() {
  const rows = [
    ["Communication",       "Phone tag &amp; email chains",     "Real-time in-app messaging"     ],
    ["Reports",             "Static PDFs weeks later",          "Live dashboard, updated instantly"],
    ["Documents",           "Fax + email attachments",          "Encrypted vault upload"          ],
    ["Progress visibility", "Ask &amp; wait for a call-back",    "10-step tracker, 24/7 visible"    ],
    ["Funding prep",        "Not included",                     "Full funding readiness score + marketplace"],
    ["Guidance",            "Reactive, generic",                "AI-personalized to your profile"],
    ["Meetings",            "Guess your specialist's schedule", "Book instantly from your dashboard"],
    ["Notifications",       "None or delayed emails",           "Real-time push + email + in-app"],
  ];
  return (
    <section className="py-20 lg:py-28 bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-300">Old way vs New way</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05]">
            Why traditional credit repair feels like a black box.
          </h2>
        </div>
        <div className="mt-12 rounded-3xl overflow-hidden border border-white/10 cq-glass-dark">
          <div className="grid grid-cols-3">
            <div className="p-5 border-b border-white/10 text-xs uppercase tracking-widest font-bold text-slate-400">Category</div>
            <div className="p-5 border-b border-l border-white/10 text-xs uppercase tracking-widest font-bold text-slate-400">Traditional Credit Repair</div>
            <div className="p-5 border-b border-l border-white/10 text-xs uppercase tracking-widest font-bold text-amber-300 bg-amber-500/5">Credit Quack</div>
            {rows.map((r, i) => (
              <React.Fragment key={i}>
                <div className="p-5 border-b border-white/10 text-sm font-bold text-white">{r[0]}</div>
                <div className="p-5 border-b border-l border-white/10 text-sm text-slate-400 flex items-center gap-2">
                  <X size={16} className="text-red-400 shrink-0" /> <span dangerouslySetInnerHTML={{ __html: r[1] }} />
                </div>
                <div className="p-5 border-b border-l border-white/10 text-sm text-white flex items-center gap-2 bg-amber-500/[0.03]">
                  <Check size={16} className="text-emerald-400 shrink-0" /> <span dangerouslySetInnerHTML={{ __html: r[2] }} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Enterprise Security — customer-facing language
   ============================================================== */
export function SecurityBlock() {
  const items = [
    { icon: ShieldAlert, t: "Enterprise-Level Security",  d: "The same security posture used by major fintech platforms — audited, monitored, and hardened." },
    { icon: Lock,        t: "Bank-Level Encryption",       d: "AES-256 encryption at rest, TLS 1.3 in transit. Your data is unreadable to anyone who shouldn't see it." },
    { icon: Server,      t: "Secure Document Storage",     d: "Files live in isolated, access-controlled cloud storage — not shared drives or email inboxes." },
    { icon: KeyRound,    t: "Protected Client Portal",     d: "Every action requires authenticated access. Session tokens rotate and expire automatically." },
    { icon: FileCheck2,  t: "Secure File Transfers",       d: "Uploads streamed over encrypted channels straight into the vault. No plain-text hops." },
    { icon: EyeOff,      t: "Private Client Communications", d: "Your messages with your specialist stay inside the platform. No external inboxes, no leaks." },
  ];
  return (
    <section className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-emerald-600">Security you don&rsquo;t have to think about</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Security First. Client Data Protected.
          </h2>
          <p className="text-slate-600 text-lg mt-4">Clients trust Credit Quack with sensitive financial documents, identity information, and credit data. The portal is designed to keep that information organized, protected, and accessible only to the right people.</p>

          {/* Security accreditation lockup */}
          <div className="mt-6 flex flex-wrap items-center gap-6" data-testid="security-accreditations">
            <img
              src={BRAND_ASSETS.mcafeeSecure}
              alt="McAfee Secure — reference only"
              className="h-10 sm:h-12 w-auto object-contain"
              loading="lazy"
            />
            <img
              src={BRAND_ASSETS.bbb}
              alt="Better Business Bureau — reference only"
              className="h-14 sm:h-16 w-auto object-contain"
              loading="lazy"
            />
            <div className="text-[10px] text-slate-500 leading-relaxed max-w-xs">
              Trust seals shown for informational reference. Credit Quack&rsquo;s security practices are audited internally &amp; align with industry-standard protections.
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {items.map((it, i) => (
            <Card key={i} className="p-6 rounded-2xl border-slate-100 cq-lift bg-white">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 grid place-items-center">
                <it.icon size={20} />
              </div>
              <h3 className="font-display text-xl font-black text-slate-900 mt-4">{it.t}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">{it.d}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Stats — animated counters
   ============================================================== */
export function StatsBlock() {
  const stats = [
    { end: 4200, suffix: "+",  label: "Clients helped"          },
    { end: 8.5,  suffix: "M",  label: "Funding assisted",       prefix: "$" , decimals: 1 },
    { end: 62000, suffix: "+", label: "Documents processed"     },
    { end: 4.9,  suffix: "/5", label: "Average client rating",   decimals: 1 },
    { end: 7,    suffix: "+",  label: "Years of experience"     },
  ];
  return (
    <section className="py-20 lg:py-24 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-6">
        {stats.map((s, i) => (
          <div key={i} className="text-center">
            <p className="font-display font-black text-slate-900 text-4xl sm:text-5xl leading-none">
              {s.prefix || ""}<Counter to={s.end} decimals={s.decimals || 0} />{s.suffix}
            </p>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mt-2">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Counter({ to, decimals = 0, duration = 1500 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(to * eased);
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 },
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to, duration]);
  const isBig = to >= 1000;
  const formatted = isBig ? Math.round(n).toLocaleString() : n.toFixed(decimals);
  return <span ref={ref}>{formatted}</span>;
}

/* ==============================================================
   Testimonials — 3 cards
   ============================================================== */
export function TestimonialsBlock() {
  const testis = [
    {
      name: "Marcus J.", role: "Small business owner",
      quote: "I could actually see my disputes moving through the system. Ended up qualifying for a $75k SBA line six months after signing up.",
      initial: "MJ", color: "from-amber-500 to-orange-500", photo: BRAND_ASSETS.headshotMarcus,
    },
    {
      name: "Priya S.", role: "First-time homebuyer",
      quote: "The AI assistant told me exactly which two cards to pay down first. Score jumped 68 points before I closed on my house.",
      initial: "PS", color: "from-sky-500 to-indigo-500", photo: BRAND_ASSETS.headshotPriya,
    },
    {
      name: "Aisha D.", role: "Realtor & investor",
      quote: "I've referred a dozen clients here. Every single one says the transparency is what makes Credit Quack different from anyone else.",
      initial: "AD", color: "from-emerald-500 to-emerald-600", photo: BRAND_ASSETS.headshotAisha,
    },
  ];
  return (
    <section className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Real stories</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            From "I'll never qualify" to "which offer should I take?"
          </h2>

          {/* Trustpilot lockup */}
          <div className="mt-6 inline-flex flex-wrap items-center gap-4 rounded-2xl bg-white border border-slate-200 px-5 py-3 shadow-sm" data-testid="trustpilot-lockup">
            <img
              src={BRAND_ASSETS.trustpilot}
              alt="Trustpilot — reference only"
              className="h-9 sm:h-10 w-auto object-contain"
              loading="lazy"
            />
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map((k) => <Star key={k} size={16} fill="#00B67A" className="text-emerald-500" />)}
              </div>
              <p className="text-sm font-bold text-slate-800">
                Rated <span className="text-emerald-600">4.9 / 5</span>
                <span className="text-slate-400 font-semibold"> · from clients who&rsquo;ve shared reviews</span>
              </p>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {testis.map((t, i) => (
            <Card key={i} className="p-7 rounded-3xl border-slate-100 cq-lift bg-white">
              <Quote size={26} className="text-amber-400" />
              <p className="text-slate-800 mt-4 leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-3 mt-6">
                {t.photo ? (
                  <img
                    src={t.photo}
                    alt={`${t.name} — client portrait`}
                    className="w-12 h-12 rounded-2xl object-cover ring-2 ring-white shadow-md"
                    loading="lazy"
                  />
                ) : (
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${t.color} text-white grid place-items-center font-display font-black`}>{t.initial}</div>
                )}
                <div>
                  <p className="font-display font-black text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[0,1,2,3,4].map((k) => <Star key={k} size={12} fill="#F59E0B" className="text-amber-500" />)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   FAQ accordion
   ============================================================== */
export function FAQBlock() {
  const faqs = [
    { q: "How long does credit repair take?",          a: "Most Credit Quack clients see meaningful improvements within 30–90 days, and the majority of dispute cycles complete within 6 months. Timelines depend on how many items are contested and how quickly bureaus respond." },
    { q: "How secure is my information?",              a: "Every file is encrypted at rest with AES-256 and transferred over TLS 1.3. Access is gated by rotating session tokens, and no sensitive data ever leaves the platform via email or fax." },
    { q: "Can I upload documents from my phone?",       a: "Yes — the client dashboard is fully mobile-optimized. Snap a photo of your ID or upload a PDF directly from your phone browser. Your specialist sees it instantly." },
    { q: "Can I message my specialist?",                a: "Absolutely. Your dashboard has a direct-message thread with your assigned specialist. All conversation history stays inside your portal — no lost emails or phone tag." },
    { q: "How often is my dashboard updated?",          a: "Instantly. Every action your specialist takes — new dispute, bureau response, document review — pushes to your dashboard the moment it happens. No manual refresh needed." },
    { q: "Can I track my funding readiness?",           a: "Yes. The Funding Center models your live profile against 21 real card and loan products so you can see, at any moment, what you'd qualify for and the exact steps to unlock the next tier." },
    { q: "Can I schedule appointments through the portal?", a: "Yes — book 30-minute consultations directly in your dashboard. Pick a reason, pick a slot, add notes. Your specialist gets an instant notification and confirms it." },
    { q: "Is there a mobile app?",                       a: "The full portal works beautifully in any modern mobile browser. Add it to your home screen and it behaves just like a native app — including push notifications." },
  ];
  return (
    <section id="faq" className="py-20 lg:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            Everything you were going to ask.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl border border-slate-100 bg-slate-50/50 data-[state=open]:bg-white data-[state=open]:shadow-sm px-5">
              <AccordionTrigger className="text-left font-display text-lg font-bold text-slate-900 py-5 hover:no-underline" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5" data-testid={`faq-content-${i}`}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ==============================================================
   Big CTA banner (mid + bottom)
   ============================================================== */
export function CTABanner({ scrollToSignup, headline, sub, cta = "Create My Free Client Portal", testid = "cta-banner-btn" }) {
  return (
    <section className="py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-[36px] overflow-hidden bg-slate-950 text-white p-10 sm:p-16 border border-white/5">
          <div className="cq-aurora absolute inset-0 opacity-80" />
          <div className="cq-dotgrid absolute inset-0 opacity-[0.08]" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-300">Ready when you are</p>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2 leading-[1.05]">{headline}</h3>
              {sub && <p className="text-slate-300 text-lg mt-3 max-w-2xl">{sub}</p>}
            </div>
            <button onClick={scrollToSignup} className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 font-black text-base h-14 px-8 shadow-lg hover:shadow-amber-500/40 transition" data-testid={testid}>
              {cta} <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ==============================================================
   Credit Data Trust Block — "Built Around the Credit Data That Matters"
   Prominent 3-bureau tile display for near-top placement.
   ============================================================== */
export function CreditDataTrustBlock() {
  return (
    <section className="py-16 lg:py-20 bg-slate-950 text-white overflow-hidden relative">
      <div className="cq-aurora absolute inset-0 opacity-60" />
      <div className="cq-dotgrid absolute inset-0 opacity-[0.06]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-300">Trust &amp; Credit Bureaus</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3 leading-[1.05]">
          Built Around the <span className="cq-text-gradient-gold">Credit Data</span> That Matters
        </h2>
        <p className="text-slate-300 text-lg mt-4 max-w-3xl mx-auto">
          Credit Quack helps clients understand, organize, and take action on information commonly reported by major credit bureaus, including Experian, Equifax, and TransUnion.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
          {[
            { name: "Experian",   src: BRAND_ASSETS.experian,   glow: "from-purple-500/15" },
            { name: "Equifax",    src: BRAND_ASSETS.equifax,    glow: "from-red-500/15"    },
            { name: "TransUnion", src: BRAND_ASSETS.transunion, glow: "from-sky-500/15"    },
          ].map((b) => (
            <div key={b.name} className={`relative rounded-3xl cq-glass-dark p-8 h-36 flex items-center justify-center cq-lift`} data-testid={`credit-data-tile-${b.name.toLowerCase()}`}>
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${b.glow} to-transparent`} />
              <img src={b.src} alt={`${b.name} logo — reference only`} className="relative max-h-14 w-auto object-contain filter brightness-0 invert opacity-90" loading="lazy" />
            </div>
          ))}
        </div>

        <p className="text-[11px] text-slate-500 mt-8 max-w-3xl mx-auto">
          Logos shown for educational and informational reference only. Credit Quack is not claiming official endorsement, sponsorship, or partnership unless specifically stated.
        </p>
      </div>
    </section>
  );
}

/* ==============================================================
   Ecosystem Block — "The Credit & Funding Ecosystem We Help You Navigate"
   Comprehensive tile grid: bureaus + banks + card brands + institutions.
   ============================================================== */
export function EcosystemBlock() {
  const tiles = [
    { name: "Experian",         src: BRAND_ASSETS.experian,              category: "Credit Bureau"   },
    { name: "Equifax",          src: BRAND_ASSETS.equifax,               category: "Credit Bureau"   },
    { name: "TransUnion",       src: BRAND_ASSETS.transunion,            category: "Credit Bureau"   },
    { name: "American Express", src: BRAND_ASSETS.amexWordmark,          category: "Card Issuer"     },
    { name: "Discover",         src: BRAND_ASSETS.discoverPersonalLoans, category: "Personal Loans"  },
    { name: "Chase",            src: null,                               category: "Bank"            },
    { name: "Capital One",      src: null,                               category: "Card Issuer"     },
    { name: "Bank of America",  src: BRAND_ASSETS.bankOfAmerica,         category: "Bank"            },
    { name: "PNC Bank",         src: BRAND_ASSETS.pnc,                   category: "Bank"            },
    { name: "TD Bank",          src: BRAND_ASSETS.tdBank,                category: "Bank"            },
    { name: "SunTrust",         src: BRAND_ASSETS.sunTrust,              category: "Bank"            },
    { name: "SBA",              src: BRAND_ASSETS.sbaLogo,               category: "Government"      },
    { name: "BBB",              src: BRAND_ASSETS.bbb,                   category: "Accreditation"   },
    { name: "Navy Federal",     src: BRAND_ASSETS.navyFederal,           category: "Credit Union"    },
    { name: "IdentityIQ",       src: BRAND_ASSETS.identityIQ,            category: "Monitoring"      },
    { name: "Dun &amp; Bradstreet", src: BRAND_ASSETS.dunBradstreet,         category: "Business Credit" },
  ];
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Partner &amp; ecosystem reference</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mt-3 leading-[1.05]">
            The Credit &amp; Funding Ecosystem <br />
            <span className="cq-text-gradient-gold">We Help You Navigate</span>
          </h2>
          <p className="text-slate-600 text-lg mt-4">We help clients prepare for and understand opportunities across the broader credit and funding ecosystem — from the three major bureaus to major card issuers, banks, credit unions, and government-backed lending programs.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
          {tiles.map((t) => (
            <div key={t.name} className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition p-5 h-28 flex flex-col items-center justify-center gap-2 group" data-testid={`ecosystem-tile-${t.name.replace(/[^a-z]/gi,'-').toLowerCase()}`}>
              {t.src ? (
                <img src={t.src} alt={`${t.name} logo — reference only`} className="max-h-10 w-auto object-contain opacity-80 group-hover:opacity-100 transition" loading="lazy" />
              ) : (
                <p className="font-display font-black text-slate-700 text-lg text-center leading-none" dangerouslySetInnerHTML={{ __html: t.name }} />
              )}
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{t.category}</p>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 mt-8 max-w-3xl">
          {REFERENCE_DISCLAIMER} Credit Quack does not use "official partner" language for any brand shown unless verified in writing.
        </p>
      </div>
    </section>
  );
}

/* ==============================================================
   Trust Badges Row — small pill badges for reuse near hero or dashboard
   ============================================================== */
export function TrustBadgesRow() {
  const badges = [
    { icon: ShieldCheck, label: "Secure Client Vault" },
    { icon: BadgeCheck,  label: "Credit Bureau Aware" },
    { icon: Rocket,      label: "Funding Readiness Engine" },
    { icon: Bot,         label: "AI-Powered Credit Insights" },
    { icon: Lock,        label: "Protected Documents" },
    { icon: KeyRound,    label: "Admin Verified" },
  ];
  return (
    <section className="py-10 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
          {badges.map((b, i) => (
            <div key={i} className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-2 hover:border-amber-300 hover:bg-white transition" data-testid={`trust-badge-${i}`}>
              <b.icon size={14} className="text-amber-600" />
              <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ==============================================================
   Secure Checkout Strip — trust badges (payment networks + guarantee seals)
   Ideal placement: right below the signup form to boost conversion.
   ============================================================== */
export function SecureCheckoutStrip() {
  return (
    <div className="max-w-4xl mx-auto mt-8 px-4">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 sm:p-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <ShieldCheck size={16} className="text-emerald-600" />
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-slate-500">
            Secure signup &middot; Guaranteed satisfaction
          </p>
        </div>
        <img
          src={BRAND_ASSETS.trustBadgesStrip}
          alt="Secure payment networks (PayPal, Visa, Mastercard, American Express, Discover) and satisfaction guarantee seals — reference only"
          className="w-full max-w-md mx-auto object-contain"
          loading="lazy"
          data-testid="trust-badges-strip"
        />
        <p className="text-[10px] text-slate-400 text-center mt-4 max-w-md mx-auto">
          Payment network + trust logos shown for informational reference only. Credit Quack is free to join and does not process payments during signup.
        </p>
      </div>
    </div>
  );
}


/* ==============================================================
   Real Approvals gallery — client-shared approval screenshots
   Positioned as social proof between Stats and Testimonials.
   Compliance-safe copy: individual results vary, no guarantee.
   ============================================================== */
const CLIENT_APPROVALS = [
  {
    src:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/sr2s14wd_images.png",
    tag:   "Chase · Business",
    detail:"Ink Business Preferred approval",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/mdqaxc1b_Screenshot%202024-08-23%20at%208.11.25%E2%80%AFPM.png",
    tag:   "Bank of America",
    detail:"Customized Cash Rewards · $25,000 limit",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/w68udq6u_Screen%20Shot%202016-12-20%20at%2010.43.47%20PM.png",
    tag:   "Discover",
    detail:"Discover it® Card · $6,500 credit line",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/jozmu7hf_approved-while-still-having-a-secured-card-v0-dc1asg1hc9kc1.webp",
    tag:   "Navy Federal",
    detail:"cashREWARDS Visa · $20,000 limit",
  },
  {
    src:   "https://customer-assets.emergentagent.com/job_quack-dashboard-pro/artifacts/7098cvn2_Venture%20this%20new.jpg",
    tag:   "Capital One",
    detail:"Venture Rewards · $10,000 limit",
  },
];

export function ClientApprovalsBlock() {
  return (
    <section className="py-20 lg:py-28 bg-slate-950 text-white relative overflow-hidden">
      <div className="cq-aurora absolute inset-0 opacity-60" />
      <div className="cq-dotgrid absolute inset-0 opacity-[0.05]" />
      <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-400/30 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
            <BadgeCheck size={12} /> Client-shared approvals
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-4 leading-[1.05]">
            Real Approvals. <br />
            <span className="cq-text-gradient-gold">Real Client Wins.</span>
          </h2>
          <p className="text-slate-300 text-lg mt-4">
            A sample of approval outcomes clients have shared with our team. Every decision is made by the issuing lender based on their own criteria — Credit Quack helps clients prepare, organize, and understand the process.
          </p>
        </div>

        {/* Approvals grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {CLIENT_APPROVALS.map((a, i) => (
            <figure
              key={i}
              className="rounded-3xl overflow-hidden bg-white border border-white/10 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)] cq-lift group"
              data-testid={`client-approval-${i}`}
            >
              <div className="relative bg-white overflow-hidden">
                <img
                  src={a.src}
                  alt={`Client-shared approval sample — ${a.tag}: ${a.detail}. Reference only.`}
                  className="w-full h-56 object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 right-3 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 flex items-center gap-1 shadow-lg">
                  <BadgeCheck size={11} /> Approved
                </div>
              </div>
              <figcaption className="p-5 bg-slate-900">
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">{a.tag}</p>
                <p className="font-display font-black text-white text-lg mt-1 leading-tight">{a.detail}</p>
              </figcaption>
            </figure>
          ))}

          {/* Highlight stat card as the 6th tile */}
          <div className="rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 text-white p-6 flex flex-col justify-between shadow-[0_20px_60px_-30px_rgba(245,158,11,0.5)] cq-lift">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/80">Sample total</p>
              <p className="font-display text-6xl font-black mt-2 leading-none">$61,500<span className="text-3xl">+</span></p>
              <p className="text-sm text-white/90 mt-3">
                in credit unlocked across the five sample approvals shown here alone. Real, client-shared outcomes.
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/20 p-3 mt-6">
              <p className="text-[10px] uppercase tracking-widest font-bold text-white/70">Also assisted</p>
              <p className="text-sm font-bold mt-1">Auto loans, SBA prep, business LOC, personal LOC, and more.</p>
            </div>
          </div>
        </div>

        {/* Compliance disclaimer */}
        <div className="mt-10 rounded-2xl bg-white/[0.04] border border-white/10 p-5 text-[11px] leading-relaxed text-slate-400 max-w-4xl">
          <p className="font-bold text-slate-200 uppercase tracking-widest text-[10px] mb-2">Individual results vary</p>
          <p>
            Approval screenshots displayed above were voluntarily shared by clients and are used with permission for illustrative purposes. Credit Quack does not guarantee approval, credit limits, interest rates, or timelines for any credit or funding product. All decisions are made solely by the issuing lender based on their own underwriting criteria, your credit profile, income, documentation, and other factors. Credit Quack is not affiliated with, endorsed by, or partnered with Chase, Bank of America, Discover, Navy Federal Credit Union, or Capital One unless specifically stated in writing.
          </p>
        </div>
      </div>
    </section>
  );
}



/* ==============================================================
   Meet the Team — Lorenzo & Xzavier, the humans behind the repairs
   ============================================================== */
export function MeetTeamBlock() {
  const team = [
    {
      name: "Lorenzo",
      title: "Head of Credit Repair",
      photo: BRAND_ASSETS.photoLorenzo,
      photoStyle: { objectPosition: "50% 15%", transform: "scale(1.35)", transformOrigin: "50% 25%" },
      quote:
        "I treat every file like it's my own credit on the line. If it can be disputed, verified, or negotiated — we're going after it.",
      stats: [
        { label: "Files worked", value: "1,200+" },
        { label: "Avg score lift", value: "+94 pts" },
        { label: "Years in credit", value: "9" },
      ],
      accent: "amber",
    },
    {
      name: "Xzavier",
      title: "Head of Funding & Client Success",
      photo: BRAND_ASSETS.photoXzavier,
      photoStyle: { objectPosition: "50% 30%" },
      quote:
        "Credit is the tool. Funding is the outcome. I make sure our clients walk out with the capital, not just a better score.",
      stats: [
        { label: "Approvals sourced", value: "3.4k+" },
        { label: "Avg funding stack", value: "$52k" },
        { label: "Years in funding", value: "7" },
      ],
      accent: "sky",
    },
    {
      name: "Mone",
      title: "Credit Repair Specialist",
      photo: BRAND_ASSETS.photoMone,
      photoStyle: { objectPosition: "50% 12%", transform: "scale(1.15)", transformOrigin: "50% 20%" },
      quote:
        "Every dispute, every round, every bureau — I stay on your file until the deletions hit and your score moves. You'll always know exactly where you stand.",
      stats: [
        { label: "Files worked", value: "800+" },
        { label: "Avg score lift", value: "+88 pts" },
        { label: "Years in credit", value: "6" },
      ],
      accent: "emerald",
    },
  ];

  const accentMap = {
    amber: { chip: "bg-amber-100 text-amber-800", ring: "ring-amber-300", dot: "bg-amber-500" },
    sky:   { chip: "bg-sky-100 text-sky-800",     ring: "ring-sky-300",   dot: "bg-sky-500"   },
    emerald: { chip: "bg-emerald-100 text-emerald-800", ring: "ring-emerald-300", dot: "bg-emerald-500" },
  };

  return (
    <section className="py-16 lg:py-20" data-testid="meet-team-block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Meet your team</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-3 leading-[1.05]">
            Real humans. Real repairs. Real funding.
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            Every Credit Quack client is worked personally by Lorenzo, Xzavier and Mone — not a call center, not a bot, not an offshore team.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {team.map((m) => {
            const a = accentMap[m.accent];
            return (
              <Card
                key={m.name}
                className="overflow-hidden rounded-3xl border-slate-200 shadow-xl bg-white cq-fade-up"
                data-testid={`team-card-${m.name.toLowerCase()}`}
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                  <img
                    src={m.photo}
                    alt={`${m.name} — ${m.title}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={m.photoStyle}
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5 text-white">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${a.chip}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${a.dot} animate-pulse`} />
                      {m.title}
                    </span>
                    <p className="font-display text-3xl sm:text-4xl font-black mt-2 leading-tight drop-shadow-lg">{m.name}</p>
                  </div>
                </div>

                <div className="p-6 sm:p-7">
                  <div className="flex items-start gap-3">
                    <Quote size={20} className="text-slate-300 shrink-0 mt-1" />
                    <p className="text-slate-700 text-[15px] leading-relaxed italic">"{m.quote}"</p>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-3 pt-5 border-t border-slate-100">
                    {m.stats.map((s) => (
                      <div key={s.label} className="text-center">
                        <p className="font-display text-2xl font-black text-slate-900">{s.value}</p>
                        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <BadgeCheck size={14} className="text-emerald-500" />
                    Personally handles your file — not outsourced
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <p className="mt-8 text-center text-xs text-slate-500 max-w-2xl mx-auto">
          Stats reflect internal performance metrics across Credit Quack client accounts. Individual results vary based on credit profile, documentation, lender criteria, and other factors. Not a guarantee of approval or score outcomes.
        </p>
      </div>
    </section>
  );
}
